import { useMemo, useState } from 'react';
import {
    Alert, Box, Button, Chip, LinearProgress, Sheet, Stack, Step,
    StepIndicator, Stepper, Typography,
} from '@mui/joy';
import { useNavigate } from 'react-router';

import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';

import { useAdvertiser } from '../../../AdvertiserContext';
import {
    APIAdsCampaignCreate, APIAdsCampaignDetail, APIAdsCampaignSubmit,
    APIAdsCampaignUpdate, APIAdsGroupCreate, APIAdsTargetingUpdate, adsErrorText,
} from '../../../api_ads';
import AdCampaignForm from '../../../components/Ads/AdCampaignForm';
import AdGroupsManager from '../../../components/Ads/AdGroupsManager';
import AdPlacementsPicker from '../../../components/Ads/AdPlacementsPicker';
import AdTargetingEditor from '../../../components/Ads/AdTargetingEditor';
import AdsAccountBar from '../../../components/Ads/AdsAccountBar';
import { useAdsAccount, useAdsPlacements } from '../../../components/Ads/useAdsData';
import { formatMoney } from '../../../components/Ads/adsConstants';
import AdGeoPricing from '../../../components/Ads/AdGeoPricing';
import AdCapacityCard from '../../../components/Ads/AdCapacityCard';



const STEPS = [
    'Настройки',
    'Площадки',
    'Аудитория',
    'Объявления',
    'Проверка',
];


export default function AdsCampaignWizardPage() {
    const navigate = useNavigate();
    const { advertiserToken: token } = useAdvertiser();
    const { account, limits, geoPricing, loading: accountLoading, error: accountError } = useAdsAccount();
    const { placements } = useAdsPlacements();

    const [step, setStep] = useState(0);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    // Черновик существует на сервере начиная со второго шага
    const [campaignId, setCampaignId] = useState(null);
    const [campaign, setCampaign] = useState(null);
    const [groupId, setGroupId] = useState(null);

    const [form, setForm] = useState({
        name: '', objective: 'traffic', pricing_model: 'cpc', bid_strategy: 'manual_cpc',
        daily_budget: '', total_budget: '', weekly_budget: '', max_avg_cpc: '',
        bid_cpc: '', bid_cpm: '', pacing: 'standard',
        date_start: null, date_end: null, schedule: {},
        freq_per_day: 5, freq_interval_sec: 600,
    });
    const [selectedPlacements, setSelectedPlacements] = useState([]);
    const [targeting, setTargeting] = useState({ geo_mode: 'all' });

    const allowedFormats = useMemo(() => ([
        ...new Set(
            placements
                .filter((p) => selectedPlacements.includes(p.code))
                .map((p) => p.format)
        ),
    ]), [placements, selectedPlacements]);

    const minBid = useMemo(() => {
        const field = form.pricing_model === 'cpc' ? 'min_cpc' : 'min_cpm';
        const chosen = placements.filter((p) => selectedPlacements.includes(p.code));
        if (!chosen.length) return 0;
        return Math.max(...chosen.map((p) => Number(p[field] || 0)));
    }, [placements, selectedPlacements, form.pricing_model]);

    const reload = async () => {
        if (!campaignId) return;
        const res = await APIAdsCampaignDetail(token, campaignId);
        setCampaign(res?.campaign || null);
    };

    // ---------- валидация шагов ----------
    const validateStep = () => {
        if (step === 0) {
            if (!form.name.trim()) return 'Укажите название кампании';
            const minDaily = Number(limits?.min_daily_budget || 0);
            if (Number(form.daily_budget) < minDaily) {
                return `Дневной бюджет не может быть меньше ${formatMoney(minDaily)} ₽`;
            }
            if (String(form.bid_strategy).startsWith('auto_') && !form.weekly_budget) {
                return 'Для автостратегии нужен недельный бюджет';
            }
        }
        if (step === 1) {
            if (!selectedPlacements.length) return 'Выберите хотя бы одну площадку';
            const bid = Number(form.pricing_model === 'cpc' ? form.bid_cpc : form.bid_cpm);
            if (bid < minBid) {
                return `Ставка ниже минимальной для выбранных площадок (${formatMoney(minBid)} ₽). `
                     + 'Вернитесь на шаг «Настройки» и повысьте её.';
            }
        }
        return null;
    };

    // ---------- переходы ----------
    const buildPayload = () => {
        const payload = {
            name: form.name.trim(),
            objective: form.objective,
            pricing_model: form.pricing_model,
            bid_strategy: form.bid_strategy,
            daily_budget: Number(form.daily_budget),
            pacing: form.pacing,
            schedule: form.schedule || {},
            freq_per_day: Number(form.freq_per_day) || 5,
            freq_interval_sec: Number(form.freq_interval_sec) || 600,
            placements: selectedPlacements,
        };
        if (form.total_budget) payload.total_budget = Number(form.total_budget);
        if (form.weekly_budget) payload.weekly_budget = Number(form.weekly_budget);
        if (form.max_avg_cpc) payload.max_avg_cpc = Number(form.max_avg_cpc);
        if (form.date_start) payload.date_start = form.date_start;
        if (form.date_end) payload.date_end = form.date_end;
        if (form.pricing_model === 'cpc') payload.bid_cpc = Number(form.bid_cpc);
        else payload.bid_cpm = Number(form.bid_cpm);
        return payload;
    };

    const next = async () => {
        const validationError = validateStep();
        if (validationError) { setError(validationError); return; }
        setError(null);

        try {
            setBusy(true);

            // Шаг «Площадки» → создаём или обновляем черновик на сервере.
            // Дальше нужны настоящие id, иначе некуда грузить объявления.
            if (step === 1) {
                if (!campaignId) {
                    const res = await APIAdsCampaignCreate(token, buildPayload());
                    const created = res?.campaign;
                    setCampaignId(created?.id);
                    setCampaign(created);
                    const groupRes = await APIAdsGroupCreate(
                        token, created.id, { name: 'Основная группа' }
                    );
                    setGroupId(groupRes?.group?.id);
                } else {
                    await APIAdsCampaignUpdate(token, campaignId, buildPayload());
                    await reload();
                }
            }

            // Шаг «Аудитория» → сохраняем таргетинг основной группы
            if (step === 2 && groupId) {
                await APIAdsTargetingUpdate(token, groupId, targeting);
                await reload();
            }

            if (step === 3) await reload();

            setStep((s) => s + 1);
        } catch (e) {
            setError(adsErrorText(e, 'Не удалось сохранить шаг'));
        } finally {
            setBusy(false);
        }
    };

    const back = () => { setError(null); setStep((s) => Math.max(0, s - 1)); };

    const submitForModeration = async () => {
        try {
            setBusy(true);
            setError(null);
            const res = await APIAdsCampaignSubmit(token, campaignId);
            navigate(`/business/${campaignId}`, {
                state: { justSubmitted: true, autoApproved: !!res?.auto_approved },
            });
        } catch (e) {
            const data = e?.response?.data;
            if (Array.isArray(data?.errors)) {
                setError(`Кампания не готова:\n• ${data.errors.join('\n• ')}`);
            } else {
                setError(adsErrorText(e));
            }
        } finally {
            setBusy(false);
        }
    };

    const structureEditable = ['draft', 'rejected', 'paused'].includes(campaign?.status);
    const allCreatives = (campaign?.groups || []).flatMap((g) => g.creatives || []);

    return (
        <Box>
            <AdsAccountBar
                account={account}
                limits={limits}
                loading={accountLoading}
                error={accountError}
            />

            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <Button
                    size="sm"
                    variant="plain"
                    color="neutral"
                    startDecorator={<ArrowBackRoundedIcon />}
                    onClick={() => navigate('/business')}
                >
                    К списку
                </Button>
                <Typography level="h4">Новая кампания</Typography>
                {campaignId && (
                    <Chip size="sm" variant="soft" color="neutral">
                        черновик #{campaignId} сохранён
                    </Chip>
                )}
            </Stack>

            <Stepper sx={{ mb: 3 }}>
                {STEPS.map((label, index) => (
                    <Step
                        key={label}
                        indicator={
                            <StepIndicator
                                variant={index <= step ? 'solid' : 'outlined'}
                                color={index < step ? 'success' : index === step ? 'primary' : 'neutral'}
                            >
                                {index < step ? <CheckRoundedIcon /> : index + 1}
                            </StepIndicator>
                        }
                    >
                        <Typography level="body-sm">{label}</Typography>
                    </Step>
                ))}
            </Stepper>

            {busy && <LinearProgress sx={{ mb: 2 }} />}
            {error && (
                <Alert color="danger" sx={{ mb: 2, whiteSpace: 'pre-line' }}>{error}</Alert>
            )}

            {/* ---------- ШАГ 1 ---------- */}
            {step === 0 && (
                <AdCampaignForm
                    value={form}
                    onChange={setForm}
                    limits={limits}
                    selectedPlacements={selectedPlacements}
                />
            )}

            {/* ---------- ШАГ 2 ---------- */}
            {step === 1 && (
                <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md' }}>
                    <Typography level="title-sm" sx={{ mb: 0.5 }}>Где показывать</Typography>
                    <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                        Под каждый формат нужно отдельное объявление — выбирать сразу все
                        площадки имеет смысл, только если готовы сделать несколько креативов.
                    </Typography>
                    <AdPlacementsPicker
                        pricingModel={form.pricing_model}
                        selected={selectedPlacements}
                        onChange={setSelectedPlacements}
                    />
                </Sheet>
            )}

            {/* ---------- ШАГ 3 ---------- */}
            {step === 2 && (
                <Box>
                    <Alert color="neutral" variant="soft" sx={{ mb: 2 }}>
                        Настраиваем аудиторию основной группы. Дополнительные группы
                        с другими условиями можно добавить на следующем шаге.
                    </Alert>
                    <AdTargetingEditor value={targeting} onChange={setTargeting} />

                    {(() => {
                        const scope = targeting.geo_mode === 'cities' ? 'city'
                            : targeting.geo_mode === 'regions' ? 'region' : 'all_russia';
                        const city = targeting.geo_mode === 'cities' ? (targeting.cities?.[0] || null) : null;
                        const region = targeting.geo_mode === 'regions' ? (targeting.regions?.[0] || null) : null;
                        return (
                            <>
                                <AdGeoPricing pricing={geoPricing} activeScope={scope} />
                                {(city || region) && (
                                    <AdCapacityCard token={token} city={city} region={region} />
                                )}
                            </>
                        );
                    })()}

                    {(() => {
                        const scope = targeting.geo_mode === 'cities' ? 'city'
                            : targeting.geo_mode === 'regions' ? 'region' : 'all_russia';
                        const city = targeting.geo_mode === 'cities' ? (targeting.cities?.[0] || null) : null;
                        const region = targeting.geo_mode === 'regions' ? (targeting.regions?.[0] || null) : null;
                        return (
                            <>
                                <AdGeoPricing pricing={geoPricing} activeScope={scope} />
                                {(city || region) && (
                                    <AdCapacityCard token={token} city={city} region={region} />
                                )}
                            </>
                        );
                    })()}
                </Box>
            )}

            {/* ---------- ШАГ 4 ---------- */}
            {step === 3 && (
                <Box>
                    {!allowedFormats.length && (
                        <Alert color="warning" sx={{ mb: 2 }}>
                            Не выбрано ни одной площадки — вернитесь на шаг «Площадки»
                        </Alert>
                    )}
                    <AdGroupsManager
                        campaign={campaign}
                        allowedFormats={allowedFormats}
                        editable={structureEditable}
                        onChanged={reload}
                    />
                </Box>
            )}

            {/* ---------- ШАГ 5 ---------- */}
            {step === 4 && campaign && (
                <Stack spacing={2}>
                    <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md' }}>
                        <Typography level="title-sm" sx={{ mb: 1.5 }}>Что получилось</Typography>
                        <Stack spacing={0.6}>
                            <Typography level="body-sm">
                                <b>{campaign.name}</b> · {campaign.groups_count || (campaign.groups || []).length} гр. ·{' '}
                                {allCreatives.length} объявл.
                            </Typography>
                            <Typography level="body-sm" color="neutral">
                                Дневной бюджет {formatMoney(campaign.daily_budget)} ₽,{' '}
                                ставка {formatMoney(
                                    campaign.pricing_model === 'cpc' ? campaign.bid_cpc : campaign.bid_cpm
                                )} ₽
                            </Typography>
                            <Typography level="body-sm" color="neutral">
                                Площадки:{' '}
                                {(campaign.campaign_placements || [])
                                    .map((l) => l.placement_name).join(', ') || '—'}
                            </Typography>
                        </Stack>
                    </Sheet>

                    {allCreatives.length === 0 && (
                        <Alert color="danger">
                            Нет ни одного объявления — кампанию не примут на модерацию.
                            Вернитесь на предыдущий шаг.
                        </Alert>
                    )}

                    <Sheet variant="soft" sx={{ p: 2, borderRadius: 'md' }}>
                        <Typography level="title-sm" sx={{ mb: 1 }}>
                            Что произойдёт после отправки
                        </Typography>
                        <Typography level="body-sm" color="neutral">
                            Кампания уйдёт на проверку модератору. Обычно это занимает
                            несколько часов. Результат придёт уведомлением в кабинет —
                            если объявление отклонят, будет указана причина.
                            Пока кампания на модерации, править её нельзя.
                        </Typography>
                    </Sheet>

                    <Button
                        size="lg"
                        startDecorator={<SendRoundedIcon />}
                        loading={busy}
                        disabled={allCreatives.length === 0}
                        onClick={submitForModeration}
                    >
                        Отправить на модерацию
                    </Button>
                </Stack>
            )}

            {/* ---------- НАВИГАЦИЯ ---------- */}
            {step < 4 && (
                <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
                    <Button
                        variant="outlined"
                        color="neutral"
                        disabled={step === 0 || busy}
                        onClick={back}
                        startDecorator={<ArrowBackRoundedIcon />}
                    >
                        Назад
                    </Button>
                    <Button
                        onClick={next}
                        loading={busy}
                        endDecorator={<ArrowForwardRoundedIcon />}
                    >
                        {step === 3 ? 'К проверке' : 'Дальше'}
                    </Button>
                    {campaignId && (
                        <Button
                            variant="plain"
                            color="neutral"
                            onClick={() => navigate(`/business/${campaignId}`)}
                        >
                            Сохранить и выйти
                        </Button>
                    )}
                </Stack>
            )}
        </Box>
    );
}